/**
 * Custom Telemetry Hooks
 * 
 * Provides convenient hooks for common telemetry scenarios in the LaserBros app.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { trackEvent, trackMetric, trackException, startTrackEvent } from './appInsights';

/**
 * Hook for tracking user authentication events
 * @returns {Object} Auth tracking methods
 */
export const useAuthTracking = () => {
  const appInsights = useAppInsightsContext();

  const trackLogin = useCallback((userId, role, method = 'email') => {
    trackEvent('User_Login', {
      userId,
      role,
      loginMethod: method,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackLogout = useCallback((userId) => {
    trackEvent('User_Logout', {
      userId,
      timestamp: new Date().toISOString()
    });
    // Clear user context from App Insights
    if (appInsights?.clearAuthenticatedUserContext) {
      appInsights.clearAuthenticatedUserContext();
    }
  }, [appInsights]);

  const trackSignup = useCallback((userId, source) => {
    trackEvent('User_Signup', {
      userId,
      signupSource: source,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackPasswordReset = useCallback((success) => {
    trackEvent('Password_Reset', {
      success,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { trackLogin, trackLogout, trackSignup, trackPasswordReset };
};

/**
 * Hook for tracking quote/RFQ events
 * @returns {Object} Quote tracking methods
 */
export const useQuoteTracking = () => {
  const trackQuoteCreated = useCallback((quoteId, itemCount, totalValue) => {
    trackEvent('Quote_Created', {
      quoteId,
      itemCount,
      totalValue,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackQuoteSubmitted = useCallback((quoteId, itemCount, totalValue) => {
    trackEvent('Quote_Submitted', {
      quoteId,
      itemCount,
      totalValue,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackQuoteAccepted = useCallback((quoteId, totalValue) => {
    trackEvent('Quote_Accepted', {
      quoteId,
      totalValue,
      timestamp: new Date().toISOString()
    });
    trackMetric('Quote_Value', totalValue, { quoteId });
  }, []);

  const trackQuoteRejected = useCallback((quoteId, reason) => {
    trackEvent('Quote_Rejected', {
      quoteId,
      rejectionReason: reason,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackRFQCreated = useCallback((rfqId, itemCount) => {
    trackEvent('RFQ_Created', {
      rfqId,
      itemCount,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { 
    trackQuoteCreated, 
    trackQuoteSubmitted, 
    trackQuoteAccepted, 
    trackQuoteRejected,
    trackRFQCreated 
  };
};

/**
 * Hook for tracking order events
 * @returns {Object} Order tracking methods
 */
export const useOrderTracking = () => {
  const trackOrderCreated = useCallback((orderId, itemCount, totalValue) => {
    trackEvent('Order_Created', {
      orderId,
      itemCount,
      totalValue,
      timestamp: new Date().toISOString()
    });
    trackMetric('Order_Value', totalValue, { orderId });
  }, []);

  const trackOrderStatusChange = useCallback((orderId, fromStatus, toStatus) => {
    trackEvent('Order_Status_Changed', {
      orderId,
      fromStatus,
      toStatus,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackOrderCompleted = useCallback((orderId, totalValue, itemCount) => {
    trackEvent('Order_Completed', {
      orderId,
      totalValue,
      itemCount,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackOrderShipped = useCallback((orderId, shippingMethod, trackingNumber) => {
    trackEvent('Order_Shipped', {
      orderId,
      shippingMethod,
      hasTrackingNumber: !!trackingNumber,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { 
    trackOrderCreated, 
    trackOrderStatusChange, 
    trackOrderCompleted,
    trackOrderShipped 
  };
};

/**
 * Hook for tracking file upload events (DXF, etc.)
 * @returns {Object} File tracking methods
 */
export const useFileTracking = () => {
  const trackFileUpload = useCallback((fileType, fileSize, success) => {
    trackEvent('File_Upload', {
      fileType,
      fileSizeKB: Math.round(fileSize / 1024),
      success,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackFileProcessing = useCallback((fileType, processingTimeMs, success) => {
    trackEvent('File_Processing', {
      fileType,
      processingTimeMs,
      success,
      timestamp: new Date().toISOString()
    });
    trackMetric('File_Processing_Time_Ms', processingTimeMs, { fileType });
  }, []);

  const trackDXFAnalysis = useCallback((success, errorType = null) => {
    trackEvent('DXF_Analysis', {
      success,
      errorType,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { trackFileUpload, trackFileProcessing, trackDXFAnalysis };
};

/**
 * Hook for tracking payment events
 * @returns {Object} Payment tracking methods
 */
export const usePaymentTracking = () => {
  const trackPaymentStarted = useCallback((orderId, amount, paymentMethod) => {
    trackEvent('Payment_Started', {
      orderId,
      amount,
      paymentMethod,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackPaymentCompleted = useCallback((orderId, amount, paymentMethod) => {
    trackEvent('Payment_Completed', {
      orderId,
      amount,
      paymentMethod,
      timestamp: new Date().toISOString()
    });
    trackMetric('Payment_Value', amount, { paymentMethod });
  }, []);

  const trackPaymentFailed = useCallback((orderId, amount, errorCode) => {
    trackEvent('Payment_Failed', {
      orderId,
      amount,
      errorCode,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { trackPaymentStarted, trackPaymentCompleted, trackPaymentFailed };
};

/**
 * Hook for tracking search and filter events
 * @returns {Object} Search tracking methods
 */
export const useSearchTracking = () => {
  const trackSearch = useCallback((searchTerm, category, resultCount) => {
    trackEvent('Search_Performed', {
      searchTerm,
      category,
      resultCount,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackFilterApplied = useCallback((filterType, filterValue, resultCount) => {
    trackEvent('Filter_Applied', {
      filterType,
      filterValue,
      resultCount,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { trackSearch, trackFilterApplied };
};

/**
 * Hook for tracking component performance
 * @param {string} componentName - Name of the component to track
 * @returns {Object} Performance tracking methods
 */
export const useComponentPerformance = (componentName) => {
  const mountTimeRef = useRef(null);

  useEffect(() => {
    mountTimeRef.current = Date.now();
    
    return () => {
      if (mountTimeRef.current) {
        const engagementTime = Date.now() - mountTimeRef.current;
        trackMetric('Component_Engagement_Time_Ms', engagementTime, {
          componentName
        });
      }
    };
  }, [componentName]);

  const trackInteraction = useCallback((interactionType, details = {}) => {
    trackEvent(`${componentName}_Interaction`, {
      interactionType,
      ...details,
      timestamp: new Date().toISOString()
    });
  }, [componentName]);

  return { trackInteraction };
};

/**
 * Hook for tracking API call performance
 * @returns {Object} API tracking methods
 */
export const useApiTracking = () => {
  const trackApiCall = useCallback((endpoint, method, duration, success, statusCode) => {
    trackEvent('API_Call', {
      endpoint,
      method,
      durationMs: duration,
      success,
      statusCode,
      timestamp: new Date().toISOString()
    });

    if (duration > 3000) {
      // Track slow API calls as a separate metric
      trackMetric('Slow_API_Call_Ms', duration, { endpoint, method });
    }
  }, []);

  const trackApiError = useCallback((endpoint, method, errorMessage, statusCode) => {
    trackException(new Error(`API Error: ${endpoint}`), {
      endpoint,
      method,
      errorMessage,
      statusCode,
      timestamp: new Date().toISOString()
    });
  }, []);

  /**
   * Creates a wrapper function that tracks API call duration
   * @param {Function} apiCall - The API call function to wrap
   * @param {string} endpoint - The endpoint being called
   * @param {string} method - HTTP method
   * @returns {Function} Wrapped API call function
   */
  const withApiTracking = useCallback((apiCall, endpoint, method = 'GET') => {
    return async (...args) => {
      const stopTracking = startTrackEvent(`API_${method}_${endpoint}`);
      const startTime = Date.now();
      
      try {
        const result = await apiCall(...args);
        const duration = Date.now() - startTime;
        trackApiCall(endpoint, method, duration, true, 200);
        stopTracking({ success: true });
        return result;
      } catch (error) {
        const duration = Date.now() - startTime;
        const statusCode = error?.response?.status || 0;
        trackApiCall(endpoint, method, duration, false, statusCode);
        trackApiError(endpoint, method, error.message, statusCode);
        stopTracking({ success: false, error: error.message });
        throw error;
      }
    };
  }, [trackApiCall, trackApiError]);

  return { trackApiCall, trackApiError, withApiTracking };
};

/**
 * Hook for tracking material/product selection
 * @returns {Object} Material tracking methods
 */
export const useMaterialTracking = () => {
  const trackMaterialSelected = useCallback((materialId, materialName, thickness) => {
    trackEvent('Material_Selected', {
      materialId,
      materialName,
      thickness,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackFinishSelected = useCallback((finishId, finishName) => {
    trackEvent('Finish_Selected', {
      finishId,
      finishName,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackQuantityChanged = useCallback((oldQuantity, newQuantity, itemId) => {
    trackEvent('Quantity_Changed', {
      oldQuantity,
      newQuantity,
      itemId,
      timestamp: new Date().toISOString()
    });
  }, []);

  return { trackMaterialSelected, trackFinishSelected, trackQuantityChanged };
};

